<template>
  <d-container fluid class="main-content-container px-4 service-group-form">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('service.services') }}</span>
        <h3 class="page-title">{{ $t('service.group.addNewServiceGroup') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/zdobienia/grupy')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('service.group.addGroup') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form :class="{ 'is-loading': !allDataLoaded }">
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="services-group-new-form" tag="form">
              <div class="form-group">
                <d-checkbox
                  inline
                  v-model="is_package"
                  toggle
                  toggle-small
                  class="mt-1">
                  {{ $t('service.isPackage') }}
                </d-checkbox>
              </div>

              <div
                v-if="is_package"
                :class="{
                  'form-control': true,
                  'is-select': true,
                  'mb-3': true
                }">
                <v-select
                  class="w-100"
                  :options="nonCalculatorServices"
                  v-model="package_services"
                  :multiple="true"
                  :reduce="service => service.id"
                  :placeholder="$t('service.selectService')"
                  label="code"
                  :filterBy="filterByForServices">
                  <template slot="no-options">
                    {{ $t('service.noServicesMatchingPhrase') }}&hellip;
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.group_name }} - {{ option.name }}
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.group_name }} - {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>

              <div
                v-if="is_package"
                class="form-group">
                <d-checkbox
                  inline
                  v-model="has_stitch_calculator"
                  toggle
                  toggle-small
                  class="mt-1">
                  {{ $t('service.hasStitchCalculator') }}
                </d-checkbox>
              </div>

              <template v-if="is_package && has_stitch_calculator">
                <div class="form-group">
                  <label>
                    {{ $t('service.calculatorType') }}:
                  </label>
                  <div class="form-control is-select">
                    <v-select
                      :options="calculatorTypes"
                      v-model="calculator_type"
                      :placeholder="$t('service.selectCalculatorType')"
                      :reduce="calculatorType => calculatorType.value"
                      :searchable="false"
                      label="label">
                    </v-select>
                  </div>
                </div>

                <div class="form-group">
                  <label>
                    {{ $t('service.calculatorServices') }}:
                  </label>
                  <div class="form-control is-select">
                    <v-select
                      class="w-100"
                      :options="services"
                      v-model="calculator_services"
                      :multiple="true"
                      :reduce="service => service.id"
                      :placeholder="$t('service.selectService')"
                      label="code"
                      :filterBy="filterByForServices">
                      <template slot="no-options">
                        {{ $t('service.noServicesMatchingPhrase') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.group_name }} - {{ option.name }}
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.group_name }} - {{ option.name }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                </div>
              </template>

              <div class="form-group">
                <label>
                  {{ $t('service.group.serviceGroupName') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('service.group.serviceGroupCode') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="code" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('service.embroidery.type') }}:
                </label>
                <div
                  class="form-control is-select">
                  <v-select
                    :options="embroideryTypes"
                    v-model="embroidery_type_id"
                    :placeholder="$t('service.chooseEmbroideryType')"
                    :reduce="embroideryType => embroideryType.id"
                    :searchable="false"
                    label="name">
                  </v-select>
                </div>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('service.group.assortmentGroupName') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="assortment_group"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="assortment_group" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('service.group.serviceGroupPhoto') }}:
                </label>

                <file-input
                  :onChange="setServiceImage"
                  id="inputFileServiceImage" />
              </div>
            </validation-observer>
          </d-tab>
          <d-tab
            :disabled="is_package"
            :title="$t('service.group.servicesVariants')">
            <p class="text-center">
              {{ $t('service.group.addingServiceVariantsAvailableAfterSave') }}
            </p>
          </d-tab>

          <d-tab :title="$t('settings.transaltion.translations')">
            <p class="text-center">
              {{ $t('service.group.addingTransialtionsAvailableAfterSave') }}
            </p>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'services-group-new',
  computed: {
    allDataLoaded () {
      return this.loadedEmbroideryItems && this.loadedServices;
    },
    embroideryTypes () {
      let langToUse = this.$store.getters.getUserLang;

      if (!this.embroideryItems || !this.embroideryItems.types) {
        return [];
      }

      return this.embroideryItems.types.map(item => {
        let availableTranslation = item.translations.filter(translation => translation.language_id === langToUse);
        let itemName = item.name;

        if (availableTranslation.length) {
          itemName = availableTranslation[0].name;
        }

        return {
          id: item.id,
          name: itemName
        };
      });
    },
    nonCalculatorServices () {
      if (this.services && this.services.length) {
        return this.services.filter(service => !service.has_stitch_calculator);
      }

      return [];
    },
    calculatorTypes () {
      return [
        { value: 'embroidery', label: this.$t('service.calculatorTypeEmbroidery') },
        { value: 'sublime', label: this.$t('service.calculatorTypeSublime') },
        { value: 'overprint', label: this.$t('service.calculatorTypeOverprint') },
        { value: 'custom', label: this.$t('service.calculatorTypeCustom') }
      ];
    }
  },
  data() {
    return {
      is_package: false,
      has_stitch_calculator: false,
      calculator_type: null,
      calculator_services: [],
      name: '',
      code: '',
      image: '',
      embroidery_type_id: null,
      assortment_group: '',
      package_services: [],
      // helpers
      services: [],
      embroideryItems: [],
      loadedServices: false,
      loadedEmbroideryItems: false,
      loadError: false
    };
  },
  mounted () {
    FormUtils.loadAdditionalData(this, {
      method: 'get',
      endpoint: '/services/all-items',
      loadedKey: 'loadedServices',
      outputKey: 'services',
      errorKey: 'loadError'
    });

    FormUtils.loadAdditionalData(this, {
      endpoint: '/embroidery/items',
      loadedKey: 'loadedEmbroideryItems',
      outputKey: 'embroideryItems',
      errorKey: 'loadError'
    });
  },
  methods: {
    filterByForServices (option, label, search) {
      return (option.code + ' ' + option.group_name + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    setServiceImage (event) {
      this.image = event.target.files[0];
    },
    validateForm () {
      FormUtils.validate(this.$refs['services-group-new-form'], this.submitForm);
    },
    submitForm () {
      let imageFields = false;
      let formDataToSend = {
        name: this.name,
        code: this.code,
        is_package: +this.is_package,
        assortment_group: this.assortment_group,
        embroidery_type_id: this.embroidery_type_id
      };

      if (this.is_package && this.package_services) {
        formDataToSend.package_services = this.package_services.map(service => service.id ? service.id : service);
      } else {
        formDataToSend.package_services = [];
      }

      if (this.is_package && this.has_stitch_calculator) {
        formDataToSend.has_stitch_calculator = 1;
        formDataToSend.calculator_type = this.calculator_type;
        formDataToSend.calculator_services = this.calculator_services ? this.calculator_services.map(service => service.id ? service.id : service) : [];
      } else {
        formDataToSend.has_stitch_calculator = 0;
        formDataToSend.calculator_type = null;
        formDataToSend.calculator_services = [];
      }

      if (this.image) {
        formDataToSend.image = this.image;
        imageFields = ['image'];
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/services/groups/store',
        formData: formDataToSend,
        successTitle: this.$t('service.group.addServiceGroupSuccessTitle'),
        successText: this.$t('service.group.addServiceGroupSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('service.group.addServiceGroupErrorText'),
        successAction: (responseData) => {
          this.$router.push('/zdobienia/grupy/edycja/' + parseInt(responseData.id, 10))
        }
      }, imageFields);
    }
  }
};
</script>

<style lang="scss" scoped>
form.is-loading {
  opacity: .25;
  pointer-events: none;
}
</style>
